import { UnlinkModal } from "@/components/modal/UnlinkModal";
import { WarningModal } from "@/components/modal/WarningModal";
import { NotFound } from "@/components/shared/NotFound";
import { CanvasSkeleton, LiveModeButtonSkeleton } from "@/components/skeletons";
import { MissingElementWarningsSkeleton } from "@/components/skeletons/MissingElementWarningSkeleton";
import { EditorActionsRightbar } from "@/components/slides/editor/EditorActionsRightbar";
import { MissingElementWarnings } from "@/components/slides/editor/MissingElementWarnings";
import { SlideCanvas } from "@/components/slides/editor/SlideCanvas";
import {
  SlideEditorProvider,
  useSlideEditorContext,
} from "@/components/slides/editor/SlideEditorContext";
import { SlideToolbar } from "@/components/slides/toolbar/SlideToolbar";
import { useSelection } from "@/contexts/SelectionContext";
import { DndProvider } from "@/lib/dnd/context/DndProvider";
import { useDnd } from "@/lib/dnd/hooks/useDnd";
import type { DragData } from "@/lib/dnd/types";
import { apolloClient } from "@/lib/graphql-client";
import { useEditorActions } from "@/lib/hooks/slides/editor/useEditorActions";
import { useSlideById } from "@/lib/hooks/slides/useSlideById";
import { convertElementsToPixels } from "@/lib/utils/elementConversion";
import { hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { useAppDispatch } from "@/store/hooks";
import { loadCanvasSize, loadSlide, setSelectedId } from "@/store/slideEditorSlice";
import type { SlideFields } from "@/types/Slide";
import type { SlideTemplate } from "@ai-teach/types";
import { isFreeformTemplate, resolveSlideTemplate } from "@ai-teach/types";
import { ErrorCodes } from "@ai-teach/types";
import { gql } from "@apollo/client";
import { DndContext } from "@dnd-kit/core";
import { useParams } from "@tanstack/react-router";
import { type ReactNode, useEffect, useMemo, useRef } from "react";
import { ActionCreators } from "redux-undo";

function SlidePageContent({
  slideId,
  draggedItem,
  children,
}: {
  slideId: string;
  draggedItem: DragData | null;
  children: ReactNode;
}) {
  const dispatch = useAppDispatch();
  const loadedSlideIdRef = useRef<string | null>(null);
  const { isWarningModalOpen, isUnlinkModalOpen } = useSelection();

  // Only fetch slide data if we're editing an existing slide
  const {
    slide: slideData,
    loading: slideLoading,
    error: slideError,
  } = useSlideById({
    slideId,
  });

  const slide = slideData;
  const { handleLeavePage, handleSave } = useEditorActions({ enableKeyboardShortcuts: true });

  // Calculate canvas type from cached data to prevent CLS on click-through navigation
  const canvasType = useMemo<"editor" | "freeform">(() => {
    // If slide is already loaded, use it
    if (slide) {
      return isFreeformTemplate(resolveSlideTemplate(slide)) ? "freeform" : "editor";
    }

    try {
      const cachedSlide = apolloClient.cache.readFragment<{
        __typename: string;
        id: string;
        type: string;
        knowledgeArea: string;
        template?: SlideTemplate | null;
      }>({
        id: `Slide:${slideId}`,
        fragment: gql`
          fragment SlideCanvasType on Slide {
            id
            type
            knowledgeArea
            template
          }
        `,
      });

      if (cachedSlide) {
        return isFreeformTemplate(resolveSlideTemplate(cachedSlide)) ? "freeform" : "editor";
      }
    } catch (error) {
      // Cache read failed, fall through to default
      console.debug("Failed to read slide from cache:", error);
    }

    // Default to editor if no data available
    return "editor";
  }, [slide, slideId]);

  const handleOutsideClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      dispatch(setSelectedId(null));
    }
  };

  // Load slide to redux state
  useEffect(() => {
    if (!slide) return;

    if (loadedSlideIdRef.current === slide.id) {
      return;
    }

    // Convert elements from percentages to pixels based on current canvas size
    const elementsInPixels = convertElementsToPixels(slide.elements, resolveSlideTemplate(slide));

    // Load entire slide with converted elements
    dispatch(
      loadSlide({
        ...slide,
        elements: elementsInPixels,
      } as SlideFields)
    );

    dispatch(ActionCreators.clearHistory());

    loadedSlideIdRef.current = slide.id;
  }, [slide, dispatch]);

  // Keyboard Event useEffect
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      // Ctrl + S => Save
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSave(true);
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [handleSave]);

  const slideNotFoundError = hasGraphQLErrorCode(slideError, ErrorCodes.NOT_FOUND);

  if (slideNotFoundError) {
    return (
      <NotFound
        title="Slide not found"
        description="This slide doesn't exist or the ID is invalid."
      />
    );
  }

  return (
    <DndProvider draggedItem={draggedItem}>
      <div className="flex flex-col h-full overflow-auto">
        {/* Main editor area - Toolbar + Canvas + Properties */}
        <div className="flex flex-1 overflow-auto relative min-h-full">
          {/* Left sidebar - Tool palette */}
          <div className="flex-shrink-0 z-[900]">
            <SlideToolbar onBack={() => handleLeavePage(false)} />
          </div>

          {/* Main canvas area */}
          <div
            onClick={handleOutsideClick}
            className="flex-1 flex justify-center items-center px-8"
          >
            {slideLoading || !slide ? (
              <div className="flex flex-col gap-3">
                <LiveModeButtonSkeleton />
                <CanvasSkeleton
                  canvasType={canvasType}
                  label={slideNotFoundError ? "Slide Not Found 404" : ""}
                />
              </div>
            ) : (
              <SlideCanvas />
            )}
          </div>

          {/* Right sidebar - Editor actions and warnings */}
          <div className="flex-shrink-0 flex flex-col h-screen z-10">
            {slideLoading || !slide ? (
              <MissingElementWarningsSkeleton />
            ) : (
              <MissingElementWarnings />
            )}
            <EditorActionsRightbar slide={slide} />
          </div>
        </div>

        {/* Modals */}
        {isUnlinkModalOpen && <UnlinkModal isOpen={true} />}
      </div>
      {isWarningModalOpen && <WarningModal isOpen={true} />}
      {children}
    </DndProvider>
  );
}

function SlidePageDndWrapper({ slideId }: { slideId: string }) {
  const {
    handleDragAbort,
    handleDragEnd,
    handleDragStart,
    Overlay,
    sensors,
    collisionDetection,
    draggedItem,
  } = useDnd();

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={handleDragAbort}
    >
      <SlidePageContent slideId={slideId} draggedItem={draggedItem}>
        {Overlay}
      </SlidePageContent>
    </DndContext>
  );
}

export function SlidePage() {
  const params = useParams({ from: "/admin/slide/$slideId" });
  const slideId = params.slideId;

  return (
    <SlideEditorProvider>
      <SlidePageDndWrapper slideId={slideId} />
    </SlideEditorProvider>
  );
}
